import React from 'react';
import GraphSource from '../../../../sources/graph';

import translations from '../../../../translations';

import { connect } from 'react-redux';

import { dismissMapElementConfig, setGroupElements as setGroupElementsAction, setAllMapElements as setAllMapElementsAction } from '../../../../actions/reduxActions/mapElements';
import { resetSelections as resetSelectionsAction } from '../../../../actions/reduxActions/graphEditor';

import { getElements } from '../../../../config/elements';
import { MAP_ELEMENT_TYPES } from '../../../../constants';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonDanger from '../../../buttons/ButtonDanger';
import { stringReplace } from '../../../../utils/core';
import { partition, pathOr, prop, sortBy, uniqBy } from 'ramda';
import Table from '../../../Table';
import GraphActions from '../../../../actions/graph';
import ConfigLayoutWrapper from '../ConfigLayoutWrapper';
import GraphStore from '../../../../stores/graph';
import connectToStores from 'alt-store-connect';
import { getFlag } from '../../../../config/flags';

/**
 * @param {String} editingToken
 * @param {String} flowId
 * @param {String} tenantId
 * @param {String} authenticationToken
 * @param {Object} groupElement The group element being deleted (all map/group elements inside will also be deleted)
 * @param {Array} mapElements List of all map elements in the flow
 * @param {Array} groupElements List of all group elements in the flow
 * @param {Function} cancel Closes the config panel and removes the entry from the store
 * @param {Function} setAllMapElements Updates all map elements in the redux store
 * @param {Function} setGroupElements Updates all group elements in the redux store
 * @param {Function} resetSelections Clears all selected map/group elements in the redux store
 *
 * @description This component allows the user to delete a group element and
 * informs the user what dependencies will be deleted if they do delete this group
 */
@connectToStores
export class GroupElementDelete extends React.Component {

    static getStores() {
        return [GraphStore];
    }

    static getPropsFromStores(props) {
        return {
            graph: GraphStore.getState().graphs[props.flowId],
        };
    }

    constructor(props) {
        super(props);
        this.state = { groupElement: {} };
    }

    componentDidMount() {
        this.setState({
            groupElement: this.props.groupElement,
            children: this.getChildren(
                [this.props.groupElement.id],
                getFlag('canvas') ? this.props.mapElements : this.props.graph.mapElements,
                getFlag('canvas') ? this.props.groupElements : this.props.graph.groupElements
            ),
        });
    }

    // given an array of group element ids, returns all their child elements
    getChildren(newGroupIds, allMapElements, currentGroupElements) {
        // find child map and group elements
        const [childMapElements, notChildMapElements] = partition(el => newGroupIds.includes(el.groupElementId), allMapElements);
        const [childGroupElements, notChildGroupElements] = partition(el => newGroupIds.includes(el.groupElementId), currentGroupElements);

        // find outcomes connected to child map elements
        const childMapIds = childMapElements.map(child => child.id);

        const childOutcomes = [
            ...childMapElements.flatMap(mapElement => mapElement.outcomes || []),
            ...notChildMapElements.flatMap(mapElement => mapElement.outcomes ? mapElement.outcomes.filter(outcome => childMapIds.includes(outcome.nextMapElementId)) : []),
        ];

        let grandchildMapElements = [];
        let grandchildGroupElements = [];
        let grandchildOutcomes = [];
        // find the children of any new group elements, removing child group elements to avoid infinite loops
        if (childGroupElements.length > 0) {
            ({ 
                mapElements: grandchildMapElements,
                groupElements: grandchildGroupElements,
                outcomes: grandchildOutcomes,
            } = this.getChildren(childGroupElements.map(group => group.id), allMapElements, notChildGroupElements));
        }
        return {
            mapElements: sortBy(prop('developerName'))(uniqBy(prop('id'), [...childMapElements, ...grandchildMapElements])),
            groupElements: sortBy(prop('developerName'))(uniqBy(prop('id'), [...childGroupElements, ...grandchildGroupElements])),
            outcomes: sortBy(prop('developerName'))(uniqBy(prop('id'), [...childOutcomes, ...grandchildOutcomes])),
        };
    }

    async deleteGroup() {
        const {
            flowId,
            cancel,
            editingToken,
            tenantId,
            authenticationToken,
            setAllMapElements,
            setGroupElements,
            resetSelections,
        } = this.props;

        await GraphSource.deleteGroupElement(this.state.groupElement.id, flowId, editingToken, tenantId, authenticationToken);

        // update old canvas
        GraphActions.get(flowId, tenantId, authenticationToken);

        // update new canvas
        resetSelections();
        const updatedMapElements = await GraphSource.getMapElements(flowId, editingToken, tenantId, authenticationToken);
        const updatedGroupElements = await GraphSource.getGroupElements(flowId, editingToken, tenantId, authenticationToken);
        // must save map elements first to avoid references to deleted groups
        setAllMapElements(updatedMapElements, flowId);
        setGroupElements(updatedGroupElements, flowId);

        cancel(flowId);
    }

    render() {
        const {
            flowId,
            cancel,
        } = this.props;

        const columns = [
            {
                renderHeader: () => 'Name',
                renderCell: ({ item }) => pathOr('', ['developerName'], item),
            },
            {
                renderHeader: () => 'Element Type',
                renderCell: ({ item }) => {
                    if (item.elementType) {
                        return getElements().find(el => el.id.toLowerCase() === item.elementType.toLowerCase()).name || item.elementType;
                    }
                    if ('nextMapElementId' in item) {
                        return getElements().find(el => el.id === MAP_ELEMENT_TYPES.outcome).name;
                    }
                    return '';
                },
            },
            {
                renderHeader: () => 'ID',
                renderCell: ({ item }) => pathOr('', ['id'], item),
            },
        ];

        const orderedChildren = [
            ...pathOr([], ['children', 'mapElements'], this.state),
            ...pathOr([], ['children', 'groupElements'], this.state),
            ...pathOr([], ['children', 'outcomes'], this.state)
        ];

        const renderBody = () => [
            <p>
                {stringReplace(
                    translations['confirm delete group element'],
                    this.state.groupElement.developerName ?? '',
                    this.state.groupElement.elementType,
                )}
                <br />
                {orderedChildren.length > 0 && stringReplace(
                    translations['delete group element dependencies'],
                    this.state.groupElement.elementType,
                )}
            </p>,
            orderedChildren.length > 0 && <Table
                wrapperClassName="table-scrollable"
                className="table"
                columns={columns}
                items={orderedChildren}
            />
        ];

        const renderFooter = () => [
            <ButtonDefault className="flex-child-right" onClick={() => cancel(flowId)}>
                {translations['cancel button']}
            </ButtonDefault>,
            <ButtonDanger
                className="margin-left"
                onClick={() => this.deleteGroup()}
            >
                {translations['delete button']}
            </ButtonDanger>
        ];

        const title = this.state.groupElement.elementType ? stringReplace(
            translations['delete heading'],
            this.state.groupElement.elementType[0].toUpperCase() + this.state.groupElement.elementType.substring(1),
        ) : '';

        return (
            <ConfigLayoutWrapper
                renderHeader={() => <h3 className="margin-top-small">{title}</h3>}
                renderBody={renderBody}
                renderFooter={renderFooter}
                title={title}
                elementType={this.state.groupElement.elementType}
                onHide={() => cancel(flowId)}
                container={this.props.container}
            />
        );
    }
}

export default connect(
    ({ currentUser, groupElements, mapElements }, ownProps) => ({
        groupElement: groupElements.find(ge => ge.id === ownProps.id),
        groupElements,
        mapElements,
        tenantId: currentUser.tenantId,
        authenticationToken: currentUser.authenticationToken,
    }),
    {
        cancel: dismissMapElementConfig,
        setAllMapElements: setAllMapElementsAction,
        setGroupElements: setGroupElementsAction,
        resetSelections: resetSelectionsAction,
    },
)(GroupElementDelete);
